import { useMutation, useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { ApiError } from '../api/client';
import type { PlaceAddress, PlacePrediction } from '../api/types';
import { useDebounced } from '../components/useDebounced';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { GoogleAttribution } from './GoogleAttribution';

// AddressAutocomplete: Straßenfeld mit Adressvorschlägen (Google Places). Die gewählte Adresse füllt Straße,
// Hausnummer, PLZ und Ort (onPick). Ist die Google-Suche nicht eingerichtet, bleibt es ein normales Eingabefeld.
export function AddressAutocomplete({
  label,
  value,
  onChange,
  onPick,
  context = '',
  className = 'field',
  country = 'DE',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onPick: (address: PlaceAddress) => void;
  // context: bereits bekannte PLZ bzw. Ort – verbessert die Vorschläge.
  context?: string;
  className?: string;
  // country: Land der Vorschläge (ISO-Code, Standard Deutschland) – z. B. Wohnort eines Eigentümers im Ausland.
  country?: string;
}) {
  const { project } = useProject();
  const papi = useProjectApi();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(() => crypto.randomUUID());
  const [unavailable, setUnavailable] = useState(false);
  const input = useDebounced([value, context].filter(Boolean).join(' ').trim(), 300);
  const suggestions = useQuery({
    queryKey: projectKeys.placesAutocomplete(project.id, input, country),
    queryFn: async ({ signal }) => {
      try {
        return await papi.placesAutocomplete(input, session, country, signal);
      } catch (e) {
        if (e instanceof ApiError && e.status === 503) setUnavailable(true);
        throw e;
      }
    },
    enabled: open && !unavailable && value.trim().length >= 3,
    staleTime: 60_000,
    retry: false,
  });
  const pick = useMutation({
    mutationFn: (p: PlacePrediction) => papi.placesAddress(p.place_id, session),
    onSuccess: (a) => {
      onPick(a);
      setOpen(false);
      setSession(crypto.randomUUID());
    },
  });
  const items = suggestions.data?.items ?? [];
  const show = open && !unavailable && value.trim().length >= 3 && items.length > 0;
  return (
    <div className={`address-autocomplete ${className}`}>
      <label className="field">
        <span>{label}</span>
        <input
          role="combobox"
          aria-expanded={show}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
          }}
        />
      </label>
      {show && (
        <div className="address-suggestions">
          <ul role="listbox" id={listId} aria-label="Adressvorschläge">
            {items.map((p) => (
              <li key={p.place_id} role="option" aria-selected={false}>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick.mutate(p)} disabled={pick.isPending}>
                  <strong>{p.main_text || p.text}</strong> <span className="muted">{p.secondary_text}</span>
                </button>
              </li>
            ))}
          </ul>
          <GoogleAttribution />
        </div>
      )}
    </div>
  );
}
